import { useCallback } from 'react';

import {
  useFetchRestDataQuery,
  useLazyFetchRestDataQuery,
} from '../restService';
import {
  type BaseQueryOptions,
  processQueryData,
  type QueryResult,
  useIndexedDBBase,
} from './useIndexedDBBase';

export interface RestQueryOptions<T, R = never> extends BaseQueryOptions<T, R> {
  endpoint: string;
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  body?: Record<string, any>;
}

export interface RestQueryCallOptions {
  endpoint: string;
}

export type LazyRestQueryTrigger = (options?: RestQueryCallOptions, forceRefetch?: boolean) => void;

export type LazyIndexedDBRestQueryResult<T, R> = [LazyRestQueryTrigger, QueryResult<T, R>];

// Non-lazy version
export function useIndexedDBRestQuery<T, R = never>({
  cacheKey,
  endpoint,
  method = 'GET',
  body,
  transform,
  skip = false,
  revalidate = false,
  cacheLifetime,
}: RestQueryOptions<T, R>): QueryResult<T, R> {
  const {
    localData,
    isLoadingCache,
    shouldFetch,
  } = useIndexedDBBase<T, R>({ cacheKey, skip, cacheLifetime });

  const skipQuery = skip || (!shouldFetch && !revalidate && localData !== null);

  const { data: apiResponse, isLoading, error } = useFetchRestDataQuery({
    url: endpoint,
    method,
    body,
    cache: true,
    cacheKey,
    cacheLifetime,
  }, {
    skip: skipQuery,
  });

  const transformedData = processQueryData(apiResponse as T, localData, transform);

  return {
    data: transformedData,
    isLoading: isLoadingCache || isLoading,
    error,
  };
}

// Lazy version
export function useLazyIndexedDBRestQuery<T, R = never>({
  cacheKey,
  endpoint,
  method = 'GET',
  body,
  transform,
  skip = false,
  cacheLifetime,
}: RestQueryOptions<T, R>): LazyIndexedDBRestQueryResult<T, R> {
  const {
    localData,
    isLoadingCache,
  } = useIndexedDBBase<T, R>({ cacheKey, skip, cacheLifetime });

  const [fetchTrigger, queryResult] = useLazyFetchRestDataQuery();

  const fetchData: LazyRestQueryTrigger = useCallback((options?: RestQueryCallOptions) => {
    fetchTrigger({
      url: options?.endpoint || endpoint,
      method,
      body,
      cache: true,
      cacheKey,
      cacheLifetime,
    });
  }, [fetchTrigger, endpoint, method, body, cacheKey, cacheLifetime]);

  const transformedData = processQueryData(queryResult.data as T, localData, transform);

  return [
    fetchData,
    {
      data: transformedData,
      isLoading: isLoadingCache || queryResult.isLoading,
      error: queryResult.error,
    },
  ];
}
